import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, ArrowUpRight, Plane, Mail, Phone, Lock, Linkedin, Instagram, Menu, X } from "lucide-react";
import { NAV, HERO, FORM, GUIDES, MEMBERS } from "@/constants/testIds";
import { GuideCheckoutModal } from "@/components/GuideCheckoutModal";
import { PackageCheckoutModal } from "@/components/PackageCheckoutModal";
import { SubscriptionModal } from "@/components/SubscriptionModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/hello-theditchcollective/30min";
const LINKEDIN_URL = process.env.REACT_APP_LINKEDIN_URL;
const INSTAGRAM_URL = process.env.REACT_APP_INSTAGRAM_URL;

const CHIP_GROUPS = {
  current_location: ["Auckland", "Wellington", "Christchurch", "Hamilton", "Dunedin", "Other NZ", "Already in Australia"],
  move_timeline: ["Within 3 months", "3–6 months", "6–12 months", "Just researching"],
  who_is_moving: ["Just me", "Me + partner", "Family with kids", "Me + pets", "Me + partner + pets"],
  melbourne_area: ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Gold Coast", "Canberra", "Hobart", "Other", "Not sure yet"],
  help_needed: [
    "Finding accommodation", "Getting a job", "Medicare & TFN setup", "Pet relocation",
    "School enrolment", "Banking & finances", "Licence transfer", "Visa questions", "Not sure yet"
  ],
  employment_situation: ["Have a job lined up", "Actively looking for work", "Working remotely for NZ employer", "Self-employed", "Not working right now"],
  accommodation_budget: ["Under $400", "$400–$600", "$600–$900", "$900+", "Not sure yet"],
  best_call_time: ["Morning 9am–12pm", "Afternoon 12–5pm", "Evening 5–7pm", "Anytime"],
  package_interest: ["Landing", "Settlement", "Full Concierge", "Family Move", "Not sure yet"],
};

function Chip({ group, value, active, onClick, multi }) {
  return (
    <button
      type="button"
      data-testid={FORM.chip(group, value)}
      data-active={active}
      onClick={onClick}
      className={`chip ${multi ? "chip-multi" : ""}`}
    >
      {active && multi && <Check size={14} className="mr-1.5" strokeWidth={3} />}
      {value}
    </button>
  );
}
function ChipGroup({ group, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {CHIP_GROUPS[group].map((v) => (
        <Chip key={v} group={group} value={v} active={value === v} onClick={() => onChange(v)} />
      ))}
    </div>
  );
}
function MultiChipGroup({ group, value, onChange }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="flex flex-wrap gap-2">
      {CHIP_GROUPS[group].map((v) => (
        <Chip key={v} group={group} value={v} active={value.includes(v)} onClick={() => toggle(v)} multi />
      ))}
    </div>
  );
}

const INIT = {
  first_name: "", last_name: "", email: "", phone_number: "",
  current_location: "", move_timeline: "", who_is_moving: "", melbourne_area: "",
  help_needed: [], employment_situation: "", accommodation_budget: "", laura_notes: "", best_call_time: "",
  package_interest: "",
};

function Field({ label, hint, num, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-3">
        {num && <span className="num-tag">{num}</span>}
        <label className="block text-sm font-semibold text-[var(--ink)]">{label}</label>
      </div>
      {hint && <p className="text-xs text-[var(--ocean)] -mt-1">{hint}</p>}
      {children}
    </div>
  );
}

export default function Landing() {
  const [form, setForm] = useState(INIT);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [checkoutGuide, setCheckoutGuide] = useState(null);
  const [checkoutPackage, setCheckoutPackage] = useState(null);
  const [subscribeTrigger, setSubscribeTrigger] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Deep link from assessment PDF + follow-up emails:
  //   /?package={id}                                 → opens checkout
  //   /?package={id}&email=…&first_name=…&last_name=… → opens pre-filled (from marketing emails)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pkgId = params.get("package");
    const PKG = {
      landing_package: { id: "landing_package", name: "Landing Package", price: 895 },
      settlement_package: { id: "settlement_package", name: "Settlement Package", price: 1895 },
      full_concierge_package: { id: "full_concierge_package", name: "Full Concierge Package", price: 3495 },
      family_move_package: { id: "family_move_package", name: "Family Move Package", price: 4995 },
      resume_review: { id: "resume_review", name: "Resume Review", price: 207 },
    };
    if (pkgId && PKG[pkgId]) {
      setCheckoutPackage({
        ...PKG[pkgId],
        prefill: {
          email: params.get("email") || "",
          first_name: params.get("first_name") || "",
          last_name: params.get("last_name") || "",
        },
      });
    }
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const scrollToForm = () => document.getElementById("intake")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const validate = () => {
    const required = [
      ["first_name", "First name"], ["last_name", "Last name"], ["email", "Email"], ["phone_number", "Phone"],
      ["move_timeline", "Move timeline"],
      ["who_is_moving", "Household type"], ["melbourne_area", "Destination city"],
    ];
    for (const [k, label] of required) {
      if (!form[k] || (Array.isArray(form[k]) && form[k].length === 0)) {
        toast.error(`Please complete: ${label}`);
        return false;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error("Please enter a valid email"); return false; }
    return true;
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Canonical Feb 2026 vocabulary — every entry point sends the same
      // field names, backend accepts legacy aliases too (target_city ⇔
      // destination_city ⇔ melbourne_area; who_is_moving ⇔ household_type;
      // current_situation ⇔ employment_situation).
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone_number: form.phone_number,
        current_location: form.current_location,
        move_timeline: form.move_timeline,
        target_city: form.melbourne_area,
        who_is_moving: form.who_is_moving,
        current_situation: form.employment_situation,
        help_needed: form.help_needed,
        support_needed: form.help_needed,
        accommodation_budget: form.accommodation_budget,
        budget_level: form.accommodation_budget,
        best_call_time: form.best_call_time,
        package_interest: form.package_interest,
        laura_notes: form.laura_notes,
      };
      await axios.post(`${API}/intake`, payload);
      setSubmitted(true);
      window.scrollTo({ top: document.getElementById("intake").offsetTop - 40, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Try again or email hello@theditchcollective.co.nz");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)] overflow-x-hidden">
      {/* ── Nav ───────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-[var(--cream)]/85 backdrop-blur-md border-b border-[var(--line)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <a href="#top" data-testid={NAV.brand} className="flex items-center gap-2.5">
            <img src="/ditch-badge.png" alt="" className="w-9 h-9" />
            <span className="hidden sm:inline font-display font-extrabold tracking-tight text-sm md:text-[15px]">
              The Ditch Collective
            </span>
          </a>
          <div className="flex items-center gap-3">
            {/* Desktop nav links */}
            <a href="#packages" className="hidden md:inline text-sm text-[var(--ink)] hover:text-[var(--ocean)]">Packages</a>
            <a href="/employment-coaching" data-testid="nav-coaching-link" className="hidden md:inline text-sm text-[var(--ink)] hover:text-[var(--ocean)]">Coaching</a>
            <a
              href="/blog"
              data-testid="nav-journal-link"
              className="hidden md:inline-flex items-baseline gap-1 text-sm text-[var(--ink)] hover:text-[var(--ocean)] transition-colors"
              title="Field notes + playbooks from the Ditch"
            >
              <span className="font-serif-italic">Journal</span>
            </a>
            <a
              href="/members"
              data-testid={MEMBERS.promoLogin}
              className="hidden sm:inline text-sm text-[var(--ink)] hover:text-[var(--ocean)]"
            >
              Sign in
            </a>
            <button
              type="button"
              data-testid={MEMBERS.promoSignup}
              onClick={() => setSubscribeTrigger({ source: "nav" })}
              className="hidden sm:inline-flex items-center gap-1 text-sm font-display font-bold rounded-full px-3 py-1.5 border border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--paper)] transition-colors"
            >
              Sign up
            </button>
            <button data-testid={NAV.cta} onClick={scrollToForm} className="btn-lime !py-2 !px-4 text-sm">
              <span className="hidden xs:inline">Get started</span>
              <span className="inline xs:hidden">Start</span>
            </button>
            {/* Mobile hamburger — only visible <640px */}
            <button
              type="button"
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
              onClick={() => setMobileMenuOpen(true)}
              className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[var(--line)] text-[var(--ink)] hover:bg-[var(--mist-soft)] transition-colors"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>

        {/* Mobile slide-down menu */}
        {mobileMenuOpen && (
          <div data-testid="mobile-menu" className="sm:hidden fixed inset-0 z-[60]" style={{backgroundColor: "#F5F3EE"}}>
            <div className="flex items-center justify-between px-5 h-16 border-b border-[var(--line)]" style={{backgroundColor: "#F5F3EE"}}>
              <a href="#top" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5">
                <img src="/ditch-badge.png" alt="" className="w-9 h-9" />
                <span className="font-display font-extrabold tracking-tight text-sm">The Ditch Collective</span>
              </a>
              <button
                type="button"
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[var(--line)] text-[var(--ink)]"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="px-5 py-8 flex flex-col gap-1 h-[calc(100vh-4rem)] overflow-y-auto" style={{backgroundColor: "#F5F3EE"}}>
              <a
                href="#packages"
                onClick={() => setMobileMenuOpen(false)}
                className="font-display text-2xl font-extrabold text-[var(--ink)] py-3 border-b border-[var(--line)]"
              >
                Packages <ArrowUpRight size={20} className="inline -mt-1 ml-1 opacity-60" />
              </a>
              <a
                href="/employment-coaching"
                data-testid="mobile-menu-coaching"
                onClick={() => setMobileMenuOpen(false)}
                className="font-display text-2xl font-extrabold text-[var(--ink)] py-3 border-b border-[var(--line)]"
              >
                Coaching <ArrowUpRight size={20} className="inline -mt-1 ml-1 opacity-60" />
              </a>
              <a
                href="#guides"
                onClick={() => setMobileMenuOpen(false)}
                className="font-display text-2xl font-extrabold text-[var(--ink)] py-3 border-b border-[var(--line)]"
              >
                Guides <ArrowUpRight size={20} className="inline -mt-1 ml-1 opacity-60" />
              </a>
              <a
                href="/blog"
                data-testid="mobile-menu-journal"
                onClick={() => setMobileMenuOpen(false)}
                className="py-3 border-b border-[var(--line)] flex items-baseline gap-2 text-[var(--ink)]"
              >
                <span className="font-serif-italic text-2xl font-normal">Journal</span>
                <ArrowUpRight size={20} className="opacity-60" />
              </a>
              <a
                href="/members"
                data-testid="mobile-menu-signin"
                className="font-display text-2xl font-extrabold text-[var(--ink)] py-3 border-b border-[var(--line)]"
              >
                Sign in <ArrowUpRight size={20} className="inline -mt-1 ml-1 opacity-60" />
              </a>
              <button
                type="button"
                data-testid="mobile-menu-signup"
                onClick={() => { setMobileMenuOpen(false); setSubscribeTrigger({ source: "mobile-menu" }); }}
                className="text-left font-display text-2xl font-extrabold text-[var(--ink)] py-3 border-b border-[var(--line)]"
              >
                Sign up — Members <ArrowUpRight size={20} className="inline -mt-1 ml-1 opacity-60" />
              </button>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="font-display text-2xl font-extrabold text-[var(--ink)] py-3 border-b border-[var(--line)]"
              >
                Book a call <ArrowUpRight size={20} className="inline -mt-1 ml-1 opacity-60" />
              </a>
              <button
                type="button"
                onClick={() => { setMobileMenuOpen(false); scrollToForm(); }}
                className="btn-lime mt-6 !py-3 !px-6 text-base w-full justify-center"
              >
                Tell us about your move <ArrowRight size={16} />
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section id="top" className="relative bg-[var(--cream)] grain overflow-hidden">
        <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--mist)] opacity-60 blur-3xl" />
        <div className="absolute top-1/3 -left-32 w-[400px] h-[400px] rounded-full bg-[var(--lime)] opacity-15 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20 md:pb-32 grid lg:grid-cols-[1.4fr_1fr] gap-12 lg:gap-20 items-center">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="num-tag">Moving Kiwis. Building Futures.</span>
              <span className="h-px w-12 bg-[var(--ocean)]" />
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[4.5rem] xl:text-[5rem] font-extrabold leading-[1.02] tracking-tight reveal reveal-1">
              Making the move to Australia? <br/>
              <span className="font-serif-italic font-normal text-[var(--ink)]">We've</span>{" "}
              <span className="lime-mark">got you.</span>
            </h1>
            <p className="text-[var(--ocean)] mt-7 text-base md:text-lg max-w-xl leading-relaxed reveal reveal-2">
              For Kiwis who want to <span className="font-serif-italic text-[var(--ink)]">move well</span>, not just move.
              Tell us about your plans and one of our team will reach out personally —
              no sales pitch, just a real conversation.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4 reveal reveal-3">
              <button data-testid={HERO.cta} onClick={scrollToForm} className="btn-ink">
                Tell us about your move
                <span className="arrow-pill"><ArrowRight size={16} strokeWidth={2.5} /></span>
              </button>
              <a href="#packages" className="text-sm font-semibold text-[var(--ink)] underline-offset-4 hover:underline">
                See the packages →
              </a>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-[var(--ocean)] reveal reveal-4">
              <Pill>Premium relocation support</Pill>
              <Pill>Based in Melbourne</Pill>
              <Pill>Free first call</Pill>
            </div>
          </div>

          {/* Hero side panel */}
          <div className="relative reveal reveal-2">
            <div className="relative bg-[var(--paper)] rounded-[2rem] p-8 md:p-10 border border-[var(--line)] shadow-[0_24px_60px_-30px_rgba(13,47,77,0.35)]">
              <div className="absolute -top-4 -right-4 bg-[var(--lime)] text-[var(--ink)] font-bold text-xs px-4 py-2 rounded-full uppercase tracking-wider">
                Free first call
              </div>
              <img src="/ditch-badge.png" alt="The Ditch Collective" className="w-44 md:w-52 mx-auto" />
              <div className="mt-6 pt-6 border-t border-dashed border-[var(--line)]">
                <p className="num-tag">The team</p>
                <p className="font-display font-bold text-lg mt-1">Trans-Tasman specialists.</p>
                <p className="text-[var(--ocean)] text-sm mt-2 leading-relaxed">
                  The Ditch Collective is a team of trans-Tasman specialists
                  who've spent years placing Kiwis into Australian roles and
                  helping them land well — recruiters with deep employer
                  relationships across the market, backed by a network of
                  property, finance, and relocation specialists who between us
                  have helped hundreds of Kiwis make the move with confidence.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="relative border-y border-[var(--line)] bg-[var(--paper)] py-4 overflow-hidden">
          <div className="marquee-track text-[var(--ink)] font-display font-semibold text-sm tracking-wider uppercase">
            {Array(2).fill(0).map((_, k) => (
              <div key={k} className="flex items-center gap-12 shrink-0">
                <span>Auckland → Melbourne</span><Dot/>
                <span className="text-[var(--ocean)]">Wellington → Sydney</span><Dot/>
                <span>Christchurch → Brisbane</span><Dot/>
                <span className="text-[var(--ocean)]">Hamilton → Gold Coast</span><Dot/>
                <span>Dunedin → Perth</span><Dot/>
                <span className="text-[var(--ocean)]">Tauranga → Adelaide</span><Dot/>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Lifestyle montage ─────────────────────────────────────────── */}
      <section className="relative bg-[var(--cream)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-10">
          <div className="grid md:grid-cols-[1fr_1.2fr] gap-8 items-end mb-10 md:mb-14">
            <div>
              <span className="num-tag">01 — Life on the other side</span>
              <h2 className="font-display text-[2.6rem] sm:text-6xl lg:text-[5rem] font-extrabold mt-3 leading-[0.96] tracking-tight">
                <span className="font-serif-italic font-normal">Sunnier</span> mornings.
              </h2>
              <p className="font-serif-italic text-3xl md:text-5xl text-[var(--ocean)] mt-2">
                Better weekends.
              </p>
            </div>
            <p className="text-[var(--ocean)] text-base md:text-lg max-w-md md:justify-self-end leading-relaxed">
              From Bondi mornings to Queensland coastlines — Aussie summers are
              closer than you think. Ocean, pools, blue skies, and weekends that actually
              feel like weekends.
            </p>
          </div>

          {/* Asymmetric grid: anchor + 2 stacked */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 md:gap-4">
            <div className="md:col-span-3 md:row-span-2 relative overflow-hidden rounded-2xl aspect-[4/5] md:aspect-auto md:min-h-[560px] group">
              <img
                src="https://images.unsplash.com/photo-1551955682-78a3c53ab544?w=1600&q=80&auto=format&fit=crop"
                alt="People on a sunny white-sand Australian beach"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[var(--ink)]/30 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">i.</p>
                <p className="font-display font-bold text-lg md:text-xl mt-1 drop-shadow">Beach mornings, every weekend.</p>
              </div>
            </div>

            <div className="md:col-span-2 relative overflow-hidden rounded-2xl aspect-[4/3] group">
              <img
                src="https://images.unsplash.com/photo-1582076197950-7a1dcdd1e07f?w=1200&q=80&auto=format&fit=crop"
                alt="A sunny beach day under blue skies"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute top-4 right-4 bg-[var(--lime)] text-[var(--ink)] text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
                Sunny side
              </div>
            </div>

            <div className="md:col-span-2 relative overflow-hidden rounded-2xl aspect-[4/3] group">
              <img
                src="https://images.unsplash.com/photo-1610263659457-27ef5a68a423?w=1200&q=80&auto=format&fit=crop"
                alt="A sun-drenched coastal city street"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute bottom-4 left-4 text-white">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-95">ii.</p>
                <p className="font-display font-bold text-base mt-1 drop-shadow">Where the street ends at the sea.</p>
              </div>
            </div>
          </div>

          {/* Cinematic strip */}
          <div className="mt-3 md:mt-4 relative overflow-hidden rounded-2xl aspect-[21/9] md:aspect-[24/7] group">
            <img
              src="https://images.unsplash.com/photo-1718750689942-9f51d49cfcea?w=2400&q=80&auto=format&fit=crop"
              alt="A wide sunny Australian coastline with a city in the distance"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)]/55 via-transparent to-[var(--ink)]/20" />
            <div className="absolute inset-0 flex items-center px-6 md:px-12">
              <div className="max-w-md text-white">
                <p className="num-tag !text-[var(--lime)] text-base">iii.</p>
                <p className="font-display text-2xl md:text-4xl font-extrabold leading-tight mt-2 drop-shadow">
                  Weekends that <span className="font-serif-italic font-normal">actually</span> feel like weekends.
                </p>
              </div>
            </div>
          </div>

          {/* Detail crop — offset, smaller */}
          <div className="mt-6 md:mt-8 grid md:grid-cols-[2fr_3fr] gap-6 md:gap-10 items-center">
            <div className="relative overflow-hidden rounded-2xl aspect-[5/4] group">
              <img
                src="https://images.unsplash.com/photo-1771392525316-1b8c9d5f42bc?w=1000&q=80&auto=format&fit=crop"
                alt="People relaxing at a sunny ocean pool"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute bottom-4 left-4 text-white">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-95">iv.</p>
                <p className="font-display font-bold text-base mt-1 drop-shadow">Ocean pools, golden afternoons.</p>
              </div>
            </div>
            <div>
              <p className="font-display text-2xl md:text-3xl font-extrabold leading-tight">
                Less rain. More <span className="font-serif-italic font-normal">light</span>.
                Same <span className="font-serif-italic font-normal">accent</span>, bigger world.
              </p>
              <p className="text-[var(--ocean)] mt-4 text-[15px] md:text-base leading-relaxed max-w-lg">
                Whether it's a Bondi morning, a Brisbane balcony or a Brunswick
                brunch — we'll help you find your version of it.
              </p>
              <button onClick={scrollToForm} className="btn-ink mt-6">
                Start your move
                <span className="arrow-pill"><ArrowRight size={16} strokeWidth={2.5} /></span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── What we do ─────────────────────────────────────────────────── */}
      <section id="help" className="relative bg-[var(--cream)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="grid md:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-end mb-14">
            <div>
              <span className="num-tag">02 — What we do</span>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold mt-3 leading-[1.05]">
                The boring bits, <br/>
                <span className="font-serif-italic font-normal">sorted.</span>
              </h2>
            </div>
            <p className="text-[var(--ocean)] text-base md:text-lg max-w-xl md:justify-self-end leading-relaxed">
              Three pillars. No upsells. We've made this move ourselves and we know
              which doors to knock on — so you don't have to.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                n: "i.",
                title: "Relocation support",
                body: "Get the admin done before you board the plane — so you land set up, not scrambling.",
                details: [
                  "Visa pathways & 444 SCV guidance",
                  "Bank account, TFN & super setup",
                  "Medicare enrolment & health cover",
                  "Driver licence transfer, step by step",
                  "Moving timeline & document checklist",
                ],
                tag: "Pre-arrival",
              },
              {
                n: "ii.",
                title: "Jobs & recruitment",
                body: "Recruitment support through The Ditch Careers — our Australian recruitment division. Active candidate representation, direct employer introductions, and job matching with suitable Australian vacancies.",
                details: [
                  "CV localised for the Australian market",
                  "Candidate registration and active representation",
                  "Direct employer and hiring-manager introductions",
                  "Interview prep and offer-negotiation support",
                  "Job matching with suitable Australian vacancies",
                ],
                tag: "Career",
              },
              {
                n: "iii.",
                title: "Settling in",
                body: "The first 90 days, sorted — suburb, schools, and a flat white that tastes like home.",
                details: [
                  "Suburb matching for your budget & lifestyle",
                  "Rental application coaching & cover letters",
                  "Schools, childcare & CCS guidance",
                  "GP signups, utilities & connections",
                  "Check-ins until you're properly settled",
                ],
                tag: "Lifestyle",
              },
            ].map((c) => (
              <div
                key={c.title}
                data-testid={`pillar-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-[var(--paper)] rounded-2xl p-8 border border-[var(--line)] flex flex-col"
              >
                <div className="flex items-center justify-between mb-7">
                  <span className="num-tag text-base">{c.n}</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--ocean)] bg-[var(--mist-soft)] px-2.5 py-1 rounded-full">{c.tag}</span>
                </div>
                <h3 className="font-display text-2xl font-bold leading-tight">{c.title}</h3>
                <p className="text-[var(--ocean)] mt-3 text-[15px] leading-relaxed">{c.body}</p>
                <ul className="mt-4 space-y-1.5 text-sm text-[var(--ink)] flex-1">
                  {c.details.map((d) => (
                    <li key={d} className="flex items-start gap-2">
                      <Check size={13} strokeWidth={3} className="mt-1 shrink-0 text-[var(--lime-deep)]" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Packages ───────────────────────────────────────────────────── */}
      <section id="packages" className="relative bg-[var(--cream)] border-t border-[var(--line)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="grid md:grid-cols-[1fr_1.4fr] gap-10 md:gap-16 items-end mb-14">
            <div>
              <span className="num-tag">03 — Packages</span>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold mt-3 leading-[1.05]">
                Move well, <br/>
                <span className="font-serif-italic font-normal">not just move.</span>
              </h2>
            </div>
            <p className="text-[var(--ocean)] text-base md:text-lg max-w-xl md:justify-self-end leading-relaxed">
              Structured, end-to-end relocation — guides, suburb matching, job
              placement, financial setup, post-arrival check-ins. Every package
              starts with a free call so we point you to the right one — no
              paying upfront.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            <PackageCard
              num="i."
              name="Landing"
              packageId="landing_package"
              onBuy={setCheckoutPackage}
              tag="Essentials"
              pitch="Prepare for the Australian employment market — the foundations, before we place you in it."
              items={[
                "Australian-market CV review and polish",
                "Job-search and salary guidance",
                "SEEK and LinkedIn setup checklist",
                "One 30-minute career call",
                "Introduction to The Ditch Careers and info on becoming a registered candidate",
                "Suburb match report",
                "Rental application pack",
                "TFN, Medicare, SIM & bank setup guides",
                "Driver licence transfer guide",
                "30-day checklist · Email support (14 days)",
              ]}
              footnote="Landing prepares you for the Australian employment market. Active candidate representation, tailored applications and employer outreach are not included."
              price={895}
            />
            <PackageCard
              num="ii."
              name="Settlement"
              packageId="settlement_package"
              onBuy={setCheckoutPackage}
              tag="Most popular"
              featured
              pitch="You won't be navigating the Australian job market alone. Our recruitment team registers you as a candidate and introduces you to suitable employers where there is a genuine match."
              items={[
                "Everything in Landing",
                "Full Australian CV rewrite",
                "LinkedIn profile review",
                "60-minute career strategy call",
                "Candidate registration with The Ditch Careers",
                "Candidate profile and career assessment",
                "Personalised job-search plan",
                "Matching with suitable Australian vacancies",
                "Direct employer and recruiter introductions",
                "CV presentation to relevant employers",
                "One interview-preparation session",
                "30 days of career and relocation support",
              ]}
              price={1895}
            />
            <PackageCard
              num="iii."
              name="Full Concierge"
              packageId="full_concierge_package"
              onBuy={setCheckoutPackage}
              tag="White glove"
              pitch="Our recruitment team actively represents you in the Australian market — identifying opportunities, submitting tailored applications, and supporting you through interviews and offers."
              items={[
                "Everything in Settlement",
                "Eight weeks of managed job-search and recruitment support",
                "Full Australian CV and LinkedIn rewrite",
                "Active candidate representation through The Ditch Careers",
                "Suitable opportunities curated and sent weekly",
                "Up to 15 tailored job applications",
                "Direct outreach to relevant employers and hiring managers",
                "Candidate profile and CV presented to suitable employers",
                "Employer and recruiter introductions",
                "Interview coordination + two prep sessions",
                "Follow-up support throughout the recruitment process",
                "Salary and offer-negotiation support",
                "KiwiSaver transfer concierge",
              ]}
              price={3495}
            />
            <PackageCard
              num="iv."
              name="Family Move"
              packageId="family_move_package"
              onBuy={setCheckoutPackage}
              tag="Whole household"
              pitch="Both adults receive personalised career and recruitment support, helping your family build employment security as part of the move."
              items={[
                "Everything in Full Concierge (for the primary earner)",
                "Career strategy and recruitment support for both adults",
                "Two Australian CV rewrites",
                "Two LinkedIn profile reviews or rewrites",
                "Candidate registration for both adults",
                "Individual career assessments and job-search strategies",
                "Active job matching through The Ditch Careers",
                "Candidate profiles presented to suitable employers",
                "Direct employer, recruiter and hiring-manager introductions",
                "Interview preparation and coordination",
                "Salary and offer-negotiation support",
                "School zone & enrolment guide · CCS setup · Removalist intros",
                "6-month family check-in",
              ]}
              price={4995}
              priceFrom
            />
          </div>

          {/* ── Recruitment disclaimer ────────────────────────────────── */}
          <div
            data-testid="packages-recruitment-disclaimer"
            className="mt-10 bg-[var(--paper)] border border-[var(--line)] rounded-2xl p-5 md:p-6 max-w-3xl mx-auto"
          >
            <p className="text-[13px] md:text-sm text-[var(--ocean)] leading-relaxed">
              Employment opportunities and introductions depend on each client's experience,
              work rights, location, industry and the vacancies available. While employment
              cannot be guaranteed, eligible clients receive genuine recruitment support
              through <strong className="text-[var(--ink)]">The Ditch Careers</strong>,
              our Australian recruitment division.
            </p>
          </div>

          {/* Resume Review — standalone add-on product */}
          <div className="mt-12 pt-10 border-t border-[var(--line)]">
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center bg-[var(--cream)] border border-[var(--line)] rounded-2xl p-6 md:p-8">
              <div>
                <span className="num-tag text-xs">Add-on · CV ready in 48h</span>
                <h3 className="font-display text-2xl md:text-3xl font-extrabold mt-3 leading-tight">
                  Resume Review <span className="font-serif-italic font-normal">&amp; Builder</span>
                </h3>
                <p className="text-[var(--ocean)] mt-2 text-sm md:text-base max-w-xl">
                  Get your CV market-ready for Australian recruiters — written feedback,
                  one revised version with track-changes, 48-hour turnaround.
                </p>
                <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[var(--ocean)]">
                  <li>· AU hiring &amp; ATS audit</li>
                  <li>· Written feedback report</li>
                  <li>· Revised version (track-changes)</li>
                  <li>· 48-hour turnaround</li>
                </ul>
              </div>
              <div className="flex flex-col items-start md:items-end gap-3">
                <div className="text-right">
                  <p className="font-display font-extrabold text-2xl text-[var(--ink)] leading-none">AU$207</p>
                  <p className="text-xs text-[var(--ocean)] mt-1">NZ$249</p>
                </div>
                <button
                  type="button"
                  data-testid="buy-resume-review"
                  onClick={() => setCheckoutPackage({ id: "resume_review", name: "Resume Review", price: 207 })}
                  className="btn-lime !py-3 !px-6 whitespace-nowrap"
                >
                  Get my CV reviewed <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* PDF guides grid */}
          <div id="guides" className="mt-16 pt-12 border-t border-[var(--line)]">
            {/* Free July tool: Pet Resume (Jelly & Pretzel sponsored) */}
            <a
              href="/pet-resume"
              data-testid="landing-pet-resume-free-cta"
              className="mb-8 rounded-3xl bg-[var(--lime)] text-[var(--ink)] p-6 md:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 hover:brightness-[.98] transition-all block group"
            >
              <div>
                <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink)]/70">
                  Free for July · Sponsored by Jelly & Pretzel
                </span>
                <p className="font-display text-xl md:text-2xl font-extrabold mt-1 leading-tight">
                  Build a Pet Resume that lands the rental
                </p>
                <p className="text-[var(--ink)]/70 text-sm mt-1 max-w-lg">
                  A one-page PDF you can attach to every Aussie rental application.
                  Photo, personality, vet & landlord refs — done in 5 minutes.
                </p>
              </div>
              <span className="inline-flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-extrabold rounded-xl px-6 py-3 whitespace-nowrap">
                Build mine — free <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            </a>

            <div className="grid md:grid-cols-[1fr_1.2fr] gap-8 items-end mb-6">
              <div>
                <span className="num-tag">PDF guides</span>
                <h3 className="font-display text-3xl md:text-4xl font-extrabold mt-3 leading-[1.05]">
                  Just need <span className="font-serif-italic font-normal">one thing</span> sorted?
                </h3>
              </div>
              <p className="text-[var(--ocean)] text-base max-w-md md:justify-self-end leading-relaxed">
                Two free guides to get you moving, then unlock the full PDF library
                as a member. Cancel any time.
              </p>
            </div>

            {/* Members upsell band */}
            <div className="mb-8 rounded-3xl bg-[var(--ink)] text-[var(--paper)] p-6 md:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--lime)]">Members</span>
                <p className="font-display text-xl md:text-2xl font-extrabold mt-1 leading-tight">
                  Unlock every PDF guide
                </p>
                <p className="text-[var(--mist)]/80 text-sm mt-1">
                  Full library access · new guides as they drop · cancel any time
                </p>
              </div>
              <button
                type="button"
                data-testid={MEMBERS.joinCta}
                onClick={() => setSubscribeTrigger({ source: "band" })}
                className="inline-flex items-center justify-center gap-2 bg-[var(--lime)] text-[var(--ink)] font-display font-extrabold rounded-xl px-6 py-3 hover:brightness-95 transition-all whitespace-nowrap"
              >
                Become a member <ArrowRight size={16} />
              </button>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                // free: instant download · members_only: subscribe to unlock · coming_soon: PDF placeholder
                { id: "should_i_move_to_australia", title: "Should I Move to Australia?", tag: "Decision", free: true },
                { id: "your_first_30_days", title: "Your First 30 Days in Australia", tag: "Arrival", free: true },
                { id: "renting_in_australia", title: "Renting in Australia", tag: "Housing", members_only: true },
                { id: "landing_your_first_aus_job", title: "Landing Your First Aus Job", tag: "Job search", members_only: true },
                { id: "cost_of_living_aus_vs_nz", title: "Cost of Living: AUS vs NZ", tag: "Money", members_only: true },
                { id: "australian_tax_for_kiwis", title: "Australian Tax for Kiwis", tag: "Money", members_only: true },
                { id: "bringing_your_pet_to_australia", title: "Bringing Your Pet to Australia", tag: "Pets", members_only: true },
                { id: "driver_licences_and_cars", title: "Driver Licences & Cars", tag: "Logistics", members_only: true },
                { id: "australian_cv_linkedin_ats", title: "Australian CV, LinkedIn & ATS Guide", tag: "Careers", members_only: true },
                { id: "how_recruitment_works_australia", title: "How Recruitment Works in Australia", tag: "Careers", members_only: true },
                { id: "qld_re_licence_transfer", title: "QLD Real Estate Licence Transfer Pack", tag: "Careers", members_only: true },
              ].map((g, i) => {
                const number = String(i + 1).padStart(2, "0");
                const Tag = (
                  <span className="text-[9px] uppercase tracking-widest font-bold text-[var(--ocean)] bg-[var(--mist-soft)] px-2 py-0.5 rounded-full">
                    {g.tag}
                  </span>
                );
                if (g.free) {
                  return (
                    <button
                      key={g.id}
                      type="button"
                      data-testid={GUIDES.card(g.id)}
                      onClick={() => setCheckoutGuide(g)}
                      className="group block w-full text-left bg-[var(--paper)] rounded-2xl p-5 border border-[var(--line)] hover:border-[var(--ink)] transition-all hover:-translate-y-1"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className="num-tag text-xs">No. {number}</span>
                        {Tag}
                      </div>
                      <p className="font-display font-bold text-base leading-snug min-h-[2.5rem]">{g.title}</p>
                      <div className="mt-4 pt-3 border-t border-dashed border-[var(--line)] flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--lime-deep)]">
                          Free download
                        </span>
                        <ArrowUpRight size={16} className="text-[var(--ink)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </div>
                    </button>
                  );
                }
                // members_only
                return (
                  <button
                    key={g.id}
                    type="button"
                    data-testid={GUIDES.card(g.id)}
                    onClick={() => setSubscribeTrigger({ title: g.title })}
                    className={`group block w-full text-left bg-[var(--paper)] rounded-2xl p-5 border transition-all hover:-translate-y-1 ${g.coming_soon ? "border-dashed border-[var(--line)] opacity-80" : "border-[var(--line)] hover:border-[var(--ink)]"}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="num-tag text-xs">No. {number}</span>
                      {Tag}
                    </div>
                    <p className="font-display font-bold text-base leading-snug min-h-[2.5rem]">{g.title}</p>
                    <div className="mt-4 pt-3 border-t border-dashed border-[var(--line)] flex items-center justify-between">
                      <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[var(--ink)]">
                        <Lock size={10} /> {g.coming_soon ? "Coming soon · Members" : "Members"}
                      </span>
                      <ArrowUpRight size={16} className="text-[var(--ink)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 text-center flex flex-wrap items-center justify-center gap-4">
              <button onClick={scrollToForm} className="btn-ghost text-sm">
                Ask which guide fits →
              </button>
              <a href="/members" className="btn-ghost text-sm">
                Already a member? Sign in →
              </a>
            </div>

            {checkoutGuide && (
              <GuideCheckoutModal guide={checkoutGuide} onClose={() => setCheckoutGuide(null)} />
            )}
            {checkoutPackage && (
              <PackageCheckoutModal pkg={checkoutPackage} onClose={() => setCheckoutPackage(null)} />
            )}
            {subscribeTrigger && (
              <SubscriptionModal trigger={subscribeTrigger} onClose={() => setSubscribeTrigger(null)} />
            )}
          </div>
        </div>
      </section>

      {/* ── Four-touch model strip ─────────────────────────────────────── */}
      <section className="relative bg-[var(--ink)] text-[var(--paper)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="grid md:grid-cols-[1fr_2fr] gap-8 md:gap-16 items-end mb-10">
            <div>
              <span className="num-tag !text-[var(--lime)]">The four-touch model</span>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold mt-3 leading-[1.05]">
                Recruit. Relocate. <br/>
                <span className="font-serif-italic font-normal text-[var(--lime)]">Refer.</span> Retain.
              </h2>
            </div>
            <p className="text-[var(--mist)] text-base md:text-lg max-w-xl md:justify-self-end leading-relaxed">
              The only home for Kiwis from the day you decide — to the day you're
              properly settled in. Four moments. One team.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden">
            {[
              { n: "01", t: "Recruit", d: "We place you into the right Australian role before you land." },
              { n: "02", t: "Relocate", d: "End-to-end support from suburb matching to your first 30 days." },
              { n: "03", t: "Refer", d: "Vetted partners for mortgages, removalists, pets, insurance and more." },
              { n: "04", t: "Retain", d: "Ongoing community, resources and support once you're settled." },
            ].map((b) => (
              <div key={b.n} className="bg-[var(--ink)] p-7 md:p-8 flex flex-col">
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-display font-extrabold text-2xl text-[var(--lime)]">{b.n}</span>
                  <span className="h-px flex-1 bg-white/15" />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold">
                  <span className="font-serif-italic font-normal">·</span> {b.t}
                </h3>
                <p className="text-[var(--mist)] mt-3 text-sm leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials (rotating) ─────────────────────────────────────── */}
      <section className="bg-[var(--cream)] border-t border-[var(--line)]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <span className="num-tag">04 — From our Kiwis</span>
          <TestimonialCarousel />
        </div>
      </section>

      {/* ── Meet the team ────────────────────────────────────────────────── */}
      <section id="team" className="bg-[var(--paper)] border-t border-[var(--line)]" data-testid="landing-team">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <span className="num-tag">05 — Meet the team</span>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold text-[var(--ink)] mt-3 max-w-3xl">
            The humans behind the move.
          </h2>
          <p className="text-[var(--ink)]/70 mt-3 max-w-2xl">
            Four Kiwis + Aussies who've made the leap — and now spend their days making yours easier.
          </p>

          <div className="mt-10 grid gap-8 md:grid-cols-2">
            {[
              {
                slug: "laura",
                name: "Laura",
                role: "Founder & Director",
                photo: "/team/laura.png",
                email: "hello@theditchcollective.co.nz",
                calendly: "https://calendly.com/hello-theditchcollective/speak-to-laura",
                bio: [
                  "Laura is the Founder and Director of The Ditch Collective — the visionary who brought the business, its purpose and its people together.",
                  "With more than 25 years across recruitment, real estate, sales, marketing and operations, Laura has an exceptional ability to spot potential, build meaningful relationships and turn ideas into action. Her career has taken her around the world, from establishing international offices to supporting community projects in South Africa and China.",
                  "The Ditch Collective is the culmination of everything Laura has built and believed in. Her vision: a smarter, softer way for Kiwis to land in Australia — one that puts people first while still delivering real, life-changing results.",
                  "Laura leads with heart, courage and infectious energy. For her, success isn't measured in moves made — it's measured in lives changed, families settled and futures built along the way.",
                ],
              },
              {
                slug: "cara",
                name: "Cara",
                role: "Co-Founder & Head of Growth",
                photo: "/team/cara.png",
                email: "cara@theditchcollective.co.nz",
                bio: [
                  "Cara is the person behind many of the ideas, partnerships and systems that keep The Ditch Collective moving forward. With five years across real estate, business development and digital marketing, she knows how to build strong relationships and turn a client's move into a well-run plan.",
                  "As Co-Founder and Head of Growth, Cara leads client relationships and business growth — working closely with every family or professional so The Ditch Collective feels like a genuine partner in their move, not just another service.",
                  "Behind the scenes, Cara also leads the company's software, member portal and internal systems, building smarter processes that make relocating simpler and less stressful. A Kiwi now living in Australia herself, she brings energy, honesty and fresh thinking to everything she builds.",
                ],
              },
              {
                slug: "priya",
                name: "Priya",
                role: "Co-Founder & Head of Careers",
                photo: "/team/priya.png",
                email: "priya@theditchcollective.co.nz",
                calendly: "https://calendly.com/careers-theditchcareers/phone-call-priya",
                bio: [
                  "With more than 18 years across recruitment, property and real estate in Australia and the UK, Priya has a genuine understanding of what makes people — and businesses — work well together on both sides of the ditch.",
                  "As Co-Founder and Head of Careers, Priya leads the job-search side of The Ditch Collective and oversees the experience of every relocating candidate and Aussie employer we work with. She takes the time to understand the person behind the CV and the business beyond the job description.",
                  "Warm, driven and naturally good with people, Priya is known for building trusted relationships and spotting potential others might miss. For her, great career support isn't about filling roles — it's about setting Kiwis up in the right role, in the right city, with people who'll actually look after them.",
                ],
              },
              {
                slug: "holly",
                name: "Holly",
                role: "Business Development & Head of Marketing",
                photo: "/team/holly.png",
                email: "holly@theditchcollective.co.nz",
                bio: [
                  "Holly brings creativity, energy and a strong people-first approach to The Ditch Collective. With a natural ability to connect with others, she introduces Kiwis to a more personal, effective way of planning their move across the ditch.",
                  "As Business Development Manager and Head of Marketing, Holly supports partnership growth while leading the company's marketing and social presence — from new employer relationships on the Aus side to campaigns that bring the brand to life.",
                  "Enthusiastic, approachable and always ready to take on a new challenge, Holly is passionate about creating genuine connections that help both our clients and The Ditch Collective grow.",
                ],
              },
            ].map((m) => (
              <article
                key={m.slug}
                data-testid={`team-card-${m.slug}`}
                className="rounded-2xl bg-[var(--cream)] border border-[var(--line)] overflow-hidden flex flex-col md:flex-row"
              >
                <div className="md:w-2/5 bg-[var(--ink)]/5">
                  <img
                    src={m.photo}
                    alt={`${m.name}, ${m.role} at The Ditch Collective`}
                    className="w-full h-64 md:h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="md:w-3/5 p-6 md:p-7 flex flex-col">
                  <h3 className="font-display text-2xl font-extrabold text-[var(--ink)]">{m.name}</h3>
                  <p className="text-xs font-bold uppercase tracking-widest text-[var(--ink)]/60 mt-1">
                    {m.role}
                  </p>
                  <div className="mt-4 space-y-3 text-sm text-[var(--ink)]/80 leading-relaxed">
                    {m.bio.map((p, i) => (<p key={i}>{p}</p>))}
                  </div>
                  <div className="mt-5 pt-4 border-t border-[var(--line)] flex flex-wrap gap-2">
                    {m.calendly && (
                      <a
                        href={m.calendly}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`team-calendly-${m.slug}`}
                        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[var(--ink)] text-[var(--paper)] hover:opacity-90"
                      >
                        Book a call
                      </a>
                    )}
                    <a
                      href={`mailto:${m.email}`}
                      data-testid={`team-email-${m.slug}`}
                      className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest border border-[var(--ink)]/30 text-[var(--ink)] hover:border-[var(--ink)]"
                    >
                      Email {m.name}
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>


      {/* ── Intake form ─────────────────────────────────────────────────── */}
      <section id="intake" data-testid={FORM.section} className="bg-[var(--paper)] border-t border-[var(--line)]">
        <div className="max-w-3xl mx-auto section-pad">
          {!submitted ? (
            <>
              <div className="mb-12">
                <span className="num-tag">05 — Over to you</span>
                <h2 className="font-display text-4xl md:text-6xl font-extrabold mt-3 leading-[1.02] tracking-tight">
                  Tell us about <span className="font-serif-italic font-normal">your move.</span>
                </h2>
                <p className="text-[var(--ocean)] mt-5 text-base md:text-lg">
                  Takes 2 minutes. Then pick a time to chat — no waiting.
                </p>
              </div>

              <form onSubmit={submit} className="space-y-9">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="First name" num="i.">
                    <input data-testid={FORM.firstName} className="input-clean"
                      value={form.first_name} onChange={(e) => set("first_name")(e.target.value)} />
                  </Field>
                  <Field label="Last name" num="ii.">
                    <input data-testid={FORM.lastName} className="input-clean"
                      value={form.last_name} onChange={(e) => set("last_name")(e.target.value)} />
                  </Field>
                </div>

                <Field label="Email address" num="iii.">
                  <input data-testid={FORM.email} type="email" className="input-clean"
                    value={form.email} onChange={(e) => set("email")(e.target.value)} />
                </Field>

                <Field label="Phone number" hint="NZ or AU number" num="iv.">
                  <input data-testid={FORM.phone} className="input-clean" placeholder="+64 21 …"
                    value={form.phone_number} onChange={(e) => set("phone_number")(e.target.value)} />
                </Field>

                <Field label="When are you planning to move?" num="v.">
                  <ChipGroup group="move_timeline" value={form.move_timeline} onChange={set("move_timeline")} />
                </Field>

                <Field label="Which Australian city?" num="vi.">
                  <ChipGroup group="melbourne_area" value={form.melbourne_area} onChange={set("melbourne_area")} />
                </Field>

                <Field label="Who's moving with you?" num="vii.">
                  <ChipGroup group="who_is_moving" value={form.who_is_moving} onChange={set("who_is_moving")} />
                </Field>

                <p className="text-xs text-[var(--ocean)] italic">
                  We'll ask the rest (budget, support areas, current city, concerns) on the pre-call questionnaire — once you've booked a time.
                </p>

                <button data-testid={FORM.submit} type="submit" disabled={submitting}
                  className="btn-ink w-full justify-center !py-4 text-base disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting ? "Sending…" : "Continue to book my time"}
                  <span className="arrow-pill"><ArrowRight size={16} strokeWidth={3} /></span>
                </button>
              </form>
            </>
          ) : (
            <div data-testid={FORM.thankYou} className="text-center py-12 md:py-20">
              <div className="w-20 h-20 mx-auto rounded-full bg-[var(--lime)] flex items-center justify-center">
                <Check size={42} className="text-[var(--ink)]" strokeWidth={3} />
              </div>
              <span className="num-tag mt-8 inline-block">Almost there</span>
              <h2 className="font-display text-4xl md:text-6xl font-extrabold mt-3 leading-[1.02]">
                Now let's pick <br className="hidden sm:block"/>
                <span className="font-serif-italic font-normal">a time, {form.first_name}.</span>
              </h2>
              <p className="text-[var(--ink)] mt-6 text-lg max-w-xl mx-auto">
                Grab a free 30-minute call with the team — the sooner we chat,
                the sooner your move stops feeling overwhelming.
              </p>
              <a
                href={`${CALENDLY_URL}?name=${encodeURIComponent(form.first_name || "")}&email=${encodeURIComponent(form.email || "")}&hide_gdpr_banner=1&primary_color=0D2F4D`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="intake-book-call"
                className="mt-8 inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-full px-8 py-4 text-lg hover:bg-[var(--ink-soft)] transition-colors"
              >
                Book your 30-min call
                <span className="arrow-pill"><ArrowRight size={16} strokeWidth={3} /></span>
              </a>
              <p className="text-[var(--ocean)] mt-6 text-sm">
                Prefer email? We'll be in touch within 48 hours too — but the call is faster.
              </p>
              <p className="text-[var(--ocean)] mt-6 text-sm">
                Follow <span className="font-semibold text-[var(--ink)]">@theditchcollective</span> for suburb guides and Kiwi life across Australia.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ── Careers cross-referral — for visitors already in AU ─────────── */}
      <section className="bg-[var(--cream)] border-t border-[var(--line)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <a
            href="https://theditchcareers.com.au"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="job-search-careers-referral"
            className="group flex items-stretch hover:opacity-95 transition-opacity"
          >
            <div className="rounded-3xl bg-[var(--ink)] text-[var(--paper)] p-6 md:p-8 flex-1 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-bold text-[var(--lime)]">
                  Already in Australia and just need a job?
                </p>
                <p className="font-display text-xl md:text-2xl font-extrabold mt-2 leading-tight">
                  Head to <span className="font-serif-italic font-normal">The Ditch Careers</span> — our dedicated recruitment site.
                </p>
                <p className="text-sm md:text-[15px] text-[var(--mist)] mt-2 max-w-2xl leading-relaxed">
                  Roles across Australia for Kiwis. CV help, interview prep, direct employer introductions — for people already settled here, no relocation needed.
                </p>
              </div>
              <span className="flex-shrink-0 inline-flex items-center gap-2 self-start md:self-auto bg-[var(--lime)] text-[var(--ink)] font-display font-bold rounded-full px-6 py-3">
                Visit Ditch Careers
                <span className="w-6 h-6 rounded-full bg-[var(--ink)] text-[var(--lime)] flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                  <ArrowRight size={12} strokeWidth={3} />
                </span>
              </span>
            </div>
          </a>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="bg-[var(--ink)] text-[var(--mist)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="grid md:grid-cols-[2fr_1fr_1fr] gap-10 mb-12">
            <div>
              <div className="bg-[var(--paper)] rounded-2xl px-6 py-4 inline-block">
                <img src="/ditch-lockup.png" alt="The Ditch Collective" className="h-14 w-auto" />
              </div>
              <p className="mt-6 max-w-md text-[15px] leading-relaxed text-[var(--mist)]/80">
                A community-driven relocation collective for Kiwis crossing the Tasman.
                Real people, real intros, real help.
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-[var(--lime)] mb-4">Contact</p>
              <a href="mailto:hello@theditchcollective.co.nz" className="flex items-center gap-2 hover:text-[var(--lime)] mb-2 text-sm">
                <Mail size={14}/> hello@theditchcollective.co.nz
              </a>
              <p className="text-sm text-[var(--mist)]/70 mb-4">Based in Melbourne</p>
              <div className="flex items-center gap-2">
                {LINKEDIN_URL && (
                  <a
                    data-testid={MEMBERS.socialLinkedin}
                    href={LINKEDIN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="The Ditch Collective on LinkedIn"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 text-[var(--mist)] hover:text-[var(--lime)] hover:border-[var(--lime)] transition-colors"
                  >
                    <Linkedin size={15} />
                  </a>
                )}
                {INSTAGRAM_URL && (
                  <a
                    data-testid={MEMBERS.socialInstagram}
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="The Ditch Collective on Instagram"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 text-[var(--mist)] hover:text-[var(--lime)] hover:border-[var(--lime)] transition-colors"
                  >
                    <Instagram size={15} />
                  </a>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-[var(--lime)] mb-4">Site</p>
              <a href="#help" className="block hover:text-[var(--lime)] mb-2 text-sm">What we do</a>
              <a href="#packages" className="block hover:text-[var(--lime)] mb-2 text-sm">Packages</a>
              <a href="/members" className="block hover:text-[var(--lime)] mb-2 text-sm">Members area</a>
              <a href="#intake" className="block hover:text-[var(--lime)] text-sm">Get started</a>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between gap-3 text-xs text-[var(--mist)]/60 md:pr-32">
            <p>© {new Date().getFullYear()} The Ditch Collective. Kiwis moving to Australia.</p>
            <p>theditchcollective.co.nz</p>
          </div>
          <div className="border-t border-white/10 mt-6 pt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-[var(--mist)]/60">
            <a href="/privacy" className="hover:text-[var(--lime)] transition-colors" data-testid="footer-privacy">Privacy Policy</a>
            <span className="text-white/20">·</span>
            <a href="/refund" className="hover:text-[var(--lime)] transition-colors" data-testid="footer-refund">Refund Policy</a>
            <span className="text-white/20">·</span>
            <a href="/disclaimer" className="hover:text-[var(--lime)] transition-colors" data-testid="footer-disclaimer">Disclaimer</a>
            <span className="text-white/20">·</span>
            <a href="/affiliate" className="hover:text-[var(--lime)] transition-colors" data-testid="footer-affiliate">Affiliate Disclosure</a>
            <span className="text-white/20">·</span>
            <a href="/third-party" className="hover:text-[var(--lime)] transition-colors" data-testid="footer-third-party">Third-Party Provider Disclaimer</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-[var(--lime)]" />
      <span className="text-[var(--ink)] font-medium">{children}</span>
    </span>
  );
}

// Placeholder testimonials — one per package — until Laura confirms real client quotes.
const TESTIMONIALS = [
  {
    package: "Landing",
    quote: `I knew exactly what I wanted to do once I landed — I just needed someone to point me at the right paperwork and stop me googling for hours. The TDC Landing pack `,
    highlight: `had every checklist, every link, every form I needed in one spot.`,
    after: ` Saved me a fortnight of admin and a real headache. Cheap as chips for what you get.`,
    name: "Jess",
    role: "Solo mover · Landing Package",
    route: "Auckland → Sydney",
    initial: "J",
  },
  {
    package: "Settlement",
    quote: `I'd been in the New Zealand property industry for years and knew I wanted to make the move to Melbourne — I just didn't know where to start. The Ditch Collective didn't just point me in the right direction, they `,
    highlight: `popped me in touch with the right people for everything — rental, references, car finance, car rental, you name it.`,
    after: ` It was way more support than I expected. By the time I landed, the rental was sorted, the car was sorted, the job was sorted. If you're serious about making the move well, not just making the move — this is who you call.`,
    name: "Maria",
    role: "Property Professional · Settlement Package",
    route: "Mount Maunganui → Melbourne",
    initial: "M",
  },
  {
    package: "Full Concierge",
    quote: `I'm a chartered accountant and I was burning out trying to job hunt across the Tasman after work. Laura's team took the whole thing off my plate — `,
    highlight: `active candidate representation, direct employer introductions, CV rewrite, interview prep, salary negotiation, even the KiwiSaver transfer.`,
    after: ` Three months later I'd accepted a role on better money than I'd ever earned at home. Worth every dollar — and honestly probably less than what one bad month back home would've cost me.`,
    name: "Tom",
    role: "Chartered Accountant · Full Concierge",
    route: "Wellington → Brisbane",
    initial: "T",
  },
  {
    package: "Family Move",
    quote: `Two kids, a dog, a partner who needed work, school zones to figure out — it was the kind of move I'd been putting off for two years because it just felt too big. The team `,
    highlight: `mapped the whole thing out for us — schools, suburbs, partner job leads, even the pet relocation.`,
    after: ` We had a virtual suburb tour, removalist intros, the lot. Six months in and the kids are settled, my partner's working, the dog is enjoying the sun. Honestly couldn't have done it without them.`,
    name: "Anna & Dave",
    role: "Family of four + dog · Family Move",
    route: "Christchurch → Gold Coast",
    initial: "A",
  },
];

function TestimonialCarousel() {
  const [i, setI] = useState(0);
  const count = TESTIMONIALS.length;

  useEffect(() => {
    if (count < 2) return;
    const id = setInterval(() => setI((x) => (x + 1) % count), 8000);
    return () => clearInterval(id);
  }, [count]);

  const t = TESTIMONIALS[i];
  const prev = () => setI((x) => (x - 1 + count) % count);
  const next = () => setI((x) => (x + 1) % count);

  return (
    <div className="mt-5" data-testid="testimonial-carousel">
      {t.package && (
        <span className="inline-block text-[10px] uppercase tracking-widest font-bold bg-[var(--mist-soft)] text-[var(--ocean)] px-2.5 py-1 rounded-full mb-4">
          Re: {t.package} Package
        </span>
      )}
      <blockquote
        key={t.quote}
        data-testid={`testimonial-${i}`}
        className="font-display text-base md:text-xl font-bold leading-[1.5] text-[var(--ink)] max-w-3xl"
        style={{ animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) both" }}
      >
        <span className="font-serif-italic font-normal text-[var(--lime)] text-2xl md:text-3xl leading-none mr-1 align-text-top">"</span>
        {t.quote}
        <span className="font-serif-italic font-normal text-[var(--ocean)]">{t.highlight}</span>
        {t.after}
        <span className="font-serif-italic font-normal text-[var(--lime)] text-2xl md:text-3xl leading-none ml-1 align-text-top">"</span>
      </blockquote>

      <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--mist)] flex items-center justify-center font-bold text-[var(--ink)] text-sm">
            {t.initial}
          </div>
          <div>
            <p className="font-semibold text-sm text-[var(--ink)]">{t.name}</p>
            <p className="text-xs text-[var(--ocean)]">{t.role} · {t.route}</p>
          </div>
        </div>

        {count > 1 && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={prev}
              data-testid="testimonial-prev"
              aria-label="Previous testimonial"
              className="w-8 h-8 rounded-full border border-[var(--line)] hover:border-[var(--ink)] inline-flex items-center justify-center text-[var(--ink)] transition-colors"
            >
              <ArrowRight size={14} className="rotate-180" />
            </button>
            <div className="flex items-center gap-2">
              {TESTIMONIALS.map((t, idx) => (
                <button
                  key={t.quote}
                  onClick={() => setI(idx)}
                  data-testid={`testimonial-dot-${idx}`}
                  aria-label={`Testimonial ${idx + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    idx === i ? "w-8 bg-[var(--ink)]" : "w-1.5 bg-[var(--line)] hover:bg-[var(--ocean)]"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={next}
              data-testid="testimonial-next"
              aria-label="Next testimonial"
              className="w-8 h-8 rounded-full border border-[var(--line)] hover:border-[var(--ink)] inline-flex items-center justify-center text-[var(--ink)] transition-colors"
            >
              <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Map of AUD price → matching NZD price (per the Sep 2026 pricing update).
// Keeps the dual-currency display consistent across the site.
const NZD_BY_AUD = {
  207: 249,
  895: 1075,
  1895: 2275,
  3495: 4195,
  4995: 5995,
};

function PackageCard({ num, name, tag, pitch, items, featured, price, packageId, onBuy, priceFrom = false, footnote = "" }) {
  const nzd = NZD_BY_AUD[price];
  return (
    <div className={`relative rounded-2xl p-7 flex flex-col border ${
      featured
        ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
        : "bg-[var(--paper)] text-[var(--ink)] border-[var(--line)] card-hover"
    }`}>
      {/* Diagonal price tag — dual currency */}
      <span
        data-testid={`package-worth-${name.toLowerCase().replace(/\s+/g, "-")}`}
        className="absolute -top-3 -right-3 rotate-6 bg-[var(--lime)] text-[var(--ink)] text-[11px] font-display font-extrabold uppercase tracking-wide px-3 py-1.5 rounded-lg shadow-md z-10 leading-tight text-center"
      >
        {priceFrom && <span className="block text-[9px] font-bold opacity-80 -mb-0.5">From</span>}
        AU${price.toLocaleString()}
        {nzd && <span className="block text-[9px] font-bold opacity-80 mt-0.5">NZ${nzd.toLocaleString()}</span>}
      </span>
      {featured && (
        <span className="absolute -top-3 left-7 bg-[var(--lime)] text-[var(--ink)] text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
          ★ Most popular
        </span>
      )}
      {/* Roman numeral only up top — tier pill sits below the name to give
          the diagonal price tag its own airspace. */}
      <div className="mb-3">
        <span className={`num-tag text-base ${featured ? "!text-[var(--lime)]" : ""}`}>{num}</span>
      </div>
      <h3 className="font-display text-2xl font-bold leading-tight">{name}</h3>
      <span className={`mt-3 self-start text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${
        featured
          ? "bg-white/10 text-[var(--lime)]"
          : "bg-[var(--mist-soft)] text-[var(--ocean)]"
      }`}>{tag}</span>
      <p className={`mt-4 text-[15px] leading-relaxed ${featured ? "text-[var(--mist)]" : "text-[var(--ocean)]"}`}>
        {pitch}
      </p>
      <ul className="mt-5 space-y-3 text-[15px] leading-snug flex-1">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2.5">
            <Check size={16} strokeWidth={3} className={`mt-1 shrink-0 ${featured ? "text-[var(--lime)]" : "text-[var(--ink)]"}`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
      {footnote && (
        <p
          data-testid={`package-footnote-${packageId}`}
          className={`mt-5 text-[12px] leading-relaxed italic ${featured ? "text-[var(--mist)]" : "text-[var(--ocean)]"}`}
        >
          {footnote}
        </p>
      )}
      <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onBuy({ id: packageId, name, price })}
          data-testid={`package-buy-${packageId}`}
          className={`font-bold text-sm inline-flex items-center justify-center gap-2 rounded-full py-3 px-4 transition-all ${
            featured
              ? "bg-[var(--lime)] text-[var(--ink)] hover:bg-[var(--lime-deep)]"
              : "bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--ink-soft)]"
          }`}
        >
          Buy now
          <ArrowRight size={14} strokeWidth={2.5} />
        </button>
        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`package-cta-${name.toLowerCase().replace(/\s+/g, "-")}`}
          className={`font-bold text-sm inline-flex items-center justify-center gap-2 rounded-full py-3 px-4 transition-all border ${
            featured
              ? "border-[var(--lime)] text-[var(--lime)] hover:bg-white/5"
              : "border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--mist-soft)]"
          }`}
        >
          Book a free call
        </a>
      </div>
    </div>
  );
}

function Dot() {
  return <span className="w-1.5 h-1.5 rounded-full bg-[var(--lime)] shrink-0" />;
}
